import { ContentHead, Page } from '../../../lib/components';
import { Outlet, To, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import {
  Dispatch,
  ElementType,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
  useCallback
} from 'react';
import { observer } from 'mobx-react-lite';
import { ContentBox } from '../../../lib/components/compound/pages/contentBox';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { ArrowBackwardIcon, CloseIcon, HelpIcon, SaveIcon } from '../../../lib/icons';
import { StepElement, VerticalStepper } from '../../../lib/components/compound/progress/stepper';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../lib/components/atomic/button';
import i18n from '../../../misc/i18n';
import { useNavigationControl } from './hooks/useNavigationControl';
import { useCustomBenefitActions } from './hooks/useCustomBenefitActions';
import { useStepNavigation } from './hooks/useStepNavigation';
import { useBeforeUnloadWarning } from './hooks/useBeforeUnloadWarning';
import { TooltipComponent } from '../../../lib/components/atomic/feedback';
import { ContentDialog } from '../../../lib/components/compound/dialogs/contentDialog';

type ButtonConfig = {
  text?: string;
  disabled?: boolean;
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  ) => void;
};

type StepperContext = {
  activeStep: number;
  setActiveStep: (step: number) => void;
  setTitle: Dispatch<SetStateAction<string | undefined>>;
  setSectionTitle: Dispatch<SetStateAction<string | undefined>>;
  setBackToTitle: Dispatch<SetStateAction<string | undefined>>;
  setBackToPath: Dispatch<SetStateAction<To | undefined>>;
  setStepperConfig: Dispatch<
    SetStateAction<Array<Omit<StepElement, 'icon' & { icon?: ElementType }>> | undefined>
  >;
  setNextDisabled: Dispatch<SetStateAction<boolean>>;
  nextDisabled: boolean;
  stepperConfig: Array<StepElement>;
  setNextButtonConfig: Dispatch<SetStateAction<ButtonConfig>>;
  setBackButtonConfig: Dispatch<SetStateAction<ButtonConfig>>;
  setShowHelpButton: Dispatch<SetStateAction<boolean>>;
  showInfoDrawer: boolean;
  setShowInfoDrawer: Dispatch<SetStateAction<boolean>>;
  setCustomCancelHandler: Dispatch<SetStateAction<((path?: To) => void) | null>>;
  setHasChangedFields: Dispatch<SetStateAction<boolean>>;
  editMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  allowNavigation: () => void;
};

export const ActionStepperPartial = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const step = useParams<{ step: string }>();

  const [title, setTitle] = useState<string>();
  const [sectionTitle, setSectionTitle] = useState<string>();
  const [backToTitle, setBackToTitle] = useState<string>(t('common:BACK').toLocaleLowerCase());
  const [backToPath, setBackToPath] = useState<To>();
  const [stepperConfig, setStepperConfig] = useState<Array<StepElement>>();
  const [nextDisabled, setNextDisabled] = useState<boolean>(false);
  const [nextButtonConfig, setNextButtonConfig] = useState<ButtonConfig>();
  const [backButtonConfig, setBackButtonConfig] = useState<ButtonConfig>();
  const [showHelpButton, setShowHelpButton] = useState<boolean>(false);
  const [showInfoDrawer, setShowInfoDrawer] = useState(false);
  const [showLeavingDialog, setShowLeavingDialog] = useState(false);
  const [customCancelHandler, setCustomCancelHandler] = useState<((path?: To) => void) | null>(
    null
  );
  const [hasChangedFields, setHasChangedFields] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);

  const { activeStep, setActiveStep: setActiveStepProxy } = useStepNavigation();

  const { newCustomBenefit, saveAsDraft, showSaveButton } = useCustomBenefitActions();

  const { allowNavigation, handleCancelClick, checkUnsavedChanges } = useNavigationControl({
    activeStep,
    stepperLength: stepperConfig?.length ?? -2,
    backToPath,
    customCancelHandler,
    hasChangedFields,
    onShowLeavingDialog: () => setShowLeavingDialog(true)
  });

  useBeforeUnloadWarning(t('custom:refresh_hint'), true, activeStep, stepperConfig?.length ?? -2);

  useEffect(() => {
    if (location.pathname.includes('edit_custom_benefit') && !newCustomBenefit.name_de) {
      navigate('/benefits/custom');
    }
  }, [location, newCustomBenefit, navigate]);

  useEffect(() => {
    const navigationEntries = performance.getEntriesByType('navigation');
    const wasRefreshed =
      navigationEntries.length > 0 &&
      (navigationEntries[0] as PerformanceNavigationTiming).type === 'reload';

    // Get id from params if available
    const { id } = step as Record<string, string | undefined>;

    if (wasRefreshed && step.step !== '1' && !id) {
      setActiveStepProxy(1);
    }
  }, []);

  const handleCloseClick = useCallback(() => {
    if (checkUnsavedChanges()) {
      setShowLeavingDialog(true);
    } else {
      handleCancelClick();
    }
  }, [checkUnsavedChanges, handleCancelClick]);

  const handleSaveAsDraft = useCallback(
    () => saveAsDraft(allowNavigation),
    [saveAsDraft, allowNavigation]
  );

  const memoizedStepperConfig = useMemo(
    () =>
      (stepperConfig &&
        Array.isArray(stepperConfig) &&
        stepperConfig.map((c, index) => {
          if (!c.icon) {
            return {
              ...c,
              icon: () => (
                <Typography paragraph marginBottom="unset">
                  {index + 1}
                </Typography>
              )
            };
          }
          return c;
        })) ||
      [],
    [stepperConfig]
  );

  const stepperContext = useMemo(
    () => ({
      setTitle,
      setSectionTitle,
      setBackToTitle,
      setBackToPath,
      setStepperConfig,
      nextDisabled,
      setNextDisabled,
      activeStep,
      setActiveStep: setActiveStepProxy,
      stepperConfig,
      setNextButtonConfig,
      setBackButtonConfig,
      setShowHelpButton,
      showInfoDrawer,
      setShowInfoDrawer,
      setCustomCancelHandler,
      setHasChangedFields,
      editMode,
      setEditMode,
      allowNavigation
    }),
    [
      setTitle,
      setSectionTitle,
      setBackToTitle,
      setBackToPath,
      setStepperConfig,
      nextDisabled,
      setNextDisabled,
      activeStep,
      setActiveStepProxy,
      stepperConfig,
      setNextButtonConfig,
      setBackButtonConfig,
      setShowHelpButton,
      showInfoDrawer,
      setShowInfoDrawer,
      setCustomCancelHandler,
      setHasChangedFields,
      editMode,
      setEditMode,
      allowNavigation
    ]
  );

  return (
    <Page className="flex flex-col">
      <Box className="flex justify-between items-center mb-8">
        <Typography variant="h1" fontWeight="bold" lineHeight="2.375rem">
          {title}
        </Typography>
        <IconButton tabIndex={-1} className="p-0" onClick={handleCloseClick}>
          <CloseIcon className="text-4xl" />
        </IconButton>
      </Box>

      <Box className="h-full grid grid-cols-[minmax(min-content,320px)_auto] gap-8">
        <Box className="w-[320px] self-start">
          <Paper className="px-4 py-6">
            <VerticalStepper
              activeStep={activeStep ? activeStep - 1 : 0}
              editMode={editMode}
              config={memoizedStepperConfig}
            />
          </Paper>
          <Button variant="secondary" onClick={handleCloseClick} className="w-full mt-6">
            {backToTitle}
          </Button>
        </Box>

        <Box className="flex flex-col mb-6">
          <ContentBox
            className="h-auto"
            headComponent={
              sectionTitle ? (
                <ContentHead
                  headline={sectionTitle}
                  showActions={true}
                  renderActions={() =>
                    showHelpButton ? (
                      <TooltipComponent title={t('common:help')} arrow placement="left">
                        <IconButton onClick={() => setShowInfoDrawer(true)}>
                          <HelpIcon />
                        </IconButton>
                      </TooltipComponent>
                    ) : (
                      <></>
                    )
                  }
                />
              ) : (
                <></>
              )
            }
            footerComponent={
              <>
                {activeStep - 1 !== stepperConfig?.length && (
                  <Box className="w-full flex justify-between items-center mx-auto gap-x-8 border-4 border-grey-200">
                    <Button
                      variant="tertiary"
                      onClick={(e) => {
                        if (backButtonConfig?.onClick) {
                          backButtonConfig?.onClick(e);
                        }
                        setNextButtonConfig(undefined);
                        setActiveStepProxy(activeStep - 1);
                      }}
                      disabled={activeStep === 1 || backButtonConfig?.disabled}
                    >
                      <ArrowBackwardIcon />
                      {t('common:BACK')}
                    </Button>

                    {showSaveButton(title) && (
                      <Button onClick={handleSaveAsDraft} variant="secondary">
                        <SaveIcon />
                        {t('receipts:STATUS_DRAFTED')}
                      </Button>
                    )}

                    <Button
                      disabled={nextDisabled}
                      trailingIcon={nextButtonConfig?.endIcon}
                      {...nextButtonConfig}
                      onClick={(e) => {
                        if (nextButtonConfig?.onClick) {
                          nextButtonConfig?.onClick(e);
                        } else {
                          setActiveStepProxy(activeStep + 1);
                        }
                      }}
                    >
                      {nextButtonConfig?.text ?? i18n.t('common:NEXT')}
                    </Button>
                  </Box>
                )}
              </>
            }
          >
            <Outlet context={stepperContext} />
          </ContentBox>
        </Box>
      </Box>

      <ContentDialog
        open={showLeavingDialog}
        title={t('common:cancelProcess')}
        variant="error"
        primaryButton={t('common:DISCARD')}
        tertiaryButton={t('common:continueEditing')}
        onClose={() => setShowLeavingDialog(false)}
        onPrimaryButtonClick={() => {
          handleCancelClick();
        }}
        onTertiaryButtonClick={() => setShowLeavingDialog(false)}
      >
        <div>{t('common:cancelProcessHint')}</div>
      </ContentDialog>
    </Page>
  );
});

export const useStepper = () => useOutletContext<StepperContext>();
